import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import apiClient from '../utils/axiosConfig';
import ConsentManagement from './ConsentManagement';
import { 
  BsEnvelope, 
  BsPerson, 
  BsCheckCircle,
  BsXCircle,
  BsShieldLock
} from 'react-icons/bs';

const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [showUnsubscribe, setShowUnsubscribe] = useState(false);
  const [unsubscribeEmail, setUnsubscribeEmail] = useState('');
  const [unsubscribeLoading, setUnsubscribeLoading] = useState(false);
  const [showConsentManagement, setShowConsentManagement] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const response = await apiClient.post('/newsletter/subscribe', {
        email,
        firstName,
        lastName
      });

      if (response.data.success) {
        toast.success('Successfully subscribed to newsletter!');
        setSubscribed(true);
        // Reset form
        setEmail('');
        setFirstName('');
        setLastName('');
      } else {
        toast.error(response.data.message || 'Failed to subscribe. Please try again.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async (e) => {
    e.preventDefault();
    setUnsubscribeLoading(true);

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(unsubscribeEmail)) {
      toast.error('Please enter a valid email address');
      setUnsubscribeLoading(false);
      return;
    }

    try {
      const response = await apiClient.post('/newsletter/unsubscribe', {
        email: unsubscribeEmail
      });

      if (response.data.success) {
        toast.success('Successfully unsubscribed from newsletter!');
        setShowUnsubscribe(false);
        setUnsubscribeEmail('');
      } else {
        toast.error(response.data.message || 'Failed to unsubscribe. Please try again.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to unsubscribe. Please try again.');
    } finally {
      setUnsubscribeLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl shadow-lg p-6 my-8">
      <div className="max-w-4xl mx-auto">
        {!subscribed ? (
          <>
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-white mb-2 fredoka">Join Our Mythological Newsletter</h3>
              <p className="text-red-100">
                Get updates on new products, special offers, and mythological stories
              </p>
            </div>
            
            {!showUnsubscribe ? (
              <form onSubmit={handleSubscribe} className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <BsPerson className="text-red-500" />
                    </div>
                    <input
                      type="text"
                      placeholder="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full pl-12 px-6 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-300"
                    />
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <BsPerson className="text-red-500" />
                    </div>
                    <input
                      type="text"
                      placeholder="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full pl-12 px-6 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-300"
                    />
                  </div>
                </div>
                
                <div className="flex-2">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <BsEnvelope className="text-red-500" />
                    </div>
                    <input
                      type="email"
                      placeholder="Your Email Address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full pl-12 px-6 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-300"
                    />
                  </div>
                </div>
                
                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full md:w-auto bg-white hover:bg-gray-100 text-red-600 font-bold py-3 px-6 rounded-lg transition duration-300 ease-in-out transform hover:scale-105 disabled:opacity-50 shadow-md"
                  >
                    {loading ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleUnsubscribe} className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <BsEnvelope className="text-red-500" />
                    </div>
                    <input
                      type="email"
                      placeholder="Your Email Address"
                      value={unsubscribeEmail}
                      onChange={(e) => setUnsubscribeEmail(e.target.value)}
                      required
                      className="w-full pl-12 px-6 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-300"
                    />
                  </div>
                </div>
                
                <div>
                  <button
                    type="submit"
                    disabled={unsubscribeLoading}
                    className="w-full md:w-auto bg-white hover:bg-gray-100 text-red-600 font-bold py-3 px-6 rounded-lg transition duration-300 ease-in-out transform hover:scale-105 disabled:opacity-50 shadow-md mr-2"
                  >
                    {unsubscribeLoading ? 'Unsubscribing...' : 'Unsubscribe'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowUnsubscribe(false)}
                    className="w-full md:w-auto bg-transparent hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300 ease-in-out border border-white mt-2 md:mt-0"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
            
            <div className="flex flex-col md:flex-row justify-between items-center mt-4 gap-2">
              <p className="text-center md:text-left text-red-100 text-sm">
                We respect your privacy. Unsubscribe at any time.
              </p>
              <div className="flex gap-3">
                {!showUnsubscribe && (
                  <button
                    onClick={() => setShowUnsubscribe(true)}
                    className="text-red-200 hover:text-white text-sm font-medium underline"
                  >
                    Want to unsubscribe?
                  </button>
                )}
                <button
                  onClick={() => setShowConsentManagement(true)}
                  className="text-red-200 hover:text-white text-sm font-medium underline flex items-center gap-1"
                >
                  <BsShieldLock /> Privacy Preferences
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                <BsCheckCircle className="text-green-500 text-3xl" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Thank You for Subscribing!</h3>
            <p className="text-red-100 mb-4">
              You've been successfully added to our newsletter. Check your email for a welcome message!
            </p>
            <button
              onClick={() => setSubscribed(false)}
              className="bg-white hover:bg-gray-100 text-red-600 font-bold py-2 px-6 rounded-lg transition duration-300 ease-in-out"
            >
              Subscribe Another Email
            </button>
          </div>
        )}
        
        {/* Consent Management Modal */}
        {showConsentManagement && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-2xl font-bold text-gray-900">Privacy Preferences</h3>
                  <button
                    onClick={() => setShowConsentManagement(false)}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    &times;
                  </button>
                </div>
                <ConsentManagement 
                  email={email || unsubscribeEmail} 
                  onConsentUpdate={() => setShowConsentManagement(false)} 
                />
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setShowConsentManagement(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Newsletter;